<script lang="ts">
	import { Card } from '$lib/components/UI';
	import type { DateFormats } from '$lib/types';
	import { Star } from 'lucide-svelte';

	type Props = {
		reviews: {
			title: string;
			rating: number;
			description: string;
			createdBy: string;
			createdAt: DateFormats;
		}[];
		tripadvisor: {
			url: string;
			totalReviews: number;
			averageRating: number;
			lastReviews: {
				title: string;
				rating: number;
				text: string;
				publishedDate: string;
				author: string;
				authorAvatar: string;
				ratingImageUrl: string;
			}[];
		} | null;
	};

	const { reviews, tripadvisor }: Props = $props();
</script>

{#if !tripadvisor}
	{#if reviews.length}
		<h3 class="text-2xl font-semibold text-secondary text-center mb-4">What Our Customers Say</h3>
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
			{#each reviews as review}
				<Card>
					<div class="flex flex-col gap-2 h-full">
						<h3 class="text-lg font-semibold text-secondary">{review.title}</h3>
						<div class="flex gap-2 text-yellow-400 dark:text-yellow-200 mb-2">
							<!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
							{#each Array(review.rating).fill(0) as _, i (i)}
								<Star fill="currentColor" strokeWidth={0} size={16} />
							{/each}
						</div>
						<p class="text-sm flex-1">{review.description}</p>
						<p class="text-sm text-secondary italic">
							{review.createdBy} - {review.createdAt.shortEnglish}
						</p>
					</div>
				</Card>
			{/each}
		</div>
	{/if}
{:else}
	<h3 class="text-2xl font-bold mb-3 px-3">Tripadvisor Reviews</h3>
	<div class="flex flex-col gap-y-4">
		{#each tripadvisor.lastReviews as review}
			<Card className="py-6">
				<div class="flex items-center gap-x-4 px-3">
					<div class="w-10 h-10 rounded-full overflow-hidden">
						<img src={review.authorAvatar} alt={review.author} />
					</div>
					<div>
						<h2 class="text-sm font-bold text-slate-500">{review.author}</h2>
						<p class="text-sm text-slate-600 dark:text-slate-400">
							{new Date(review.publishedDate).toLocaleDateString('en-US', {
								year: 'numeric',
								month: 'long',
								day: 'numeric'
							})}
						</p>
					</div>
				</div>
				<div class="pt-3">
					<img src={review.ratingImageUrl} alt={review.title} />
				</div>
				<div class="flex flex-col gap-y-2 px-3 pt-3">
					<h2 class="text-lg font-bold">{review.title}</h2>
					<p class="text-sm text-slate-700 dark:text-slate-300 italic">{review.text}</p>
				</div>
			</Card>
		{/each}
		<div class="px-2">
			<a
				href={tripadvisor.url}
				class="text-sm text-slate-500 dark:text-slate-400"
				target="_blank"
				rel="noopener noreferrer"
			>
				View all {tripadvisor.totalReviews} reviews on Tripadvisor &rarr;
			</a>
		</div>
	</div>
{/if}
